import { useCallback, useMemo } from 'react';
import { useLocation } from '@tanstack/react-router';

import { FEN_STARTING_POSITION } from '@libs/chess/constants';
import { useTranslate } from '@providers/TranslateProvider';

import { useAnonymousSecret } from '@features/auth/hooks/useAnonymousSecret';
import { useAuth } from '@features/auth/hooks/useAuth';
import { formatGameSpecs } from '@features/game/utils/formatGameSpecs';
import { useDeleteInviteMutation, useMyInvitesQuery } from '@features/shared/api/fastgames';
import { useDeviceId } from '@features/shared/hooks/useDeviceId';
import { SiteLogoWidget } from '@features/shared/ui/SiteLogo/SiteLogoWidget';
import { UserAuthMenuWidget } from '@features/user/ui/UserAuthMenu/UserAuthMenuWidget';
import { UserSettingsMenuWidget } from '@features/user/ui/UserSettingsMenu/UserSettingsMenuWidget';

import { FriendMyGamesPage, IInviteInfo } from './FriendMyGamesPage';

export function FriendMyGamesPageWidget() {
  const { t } = useTranslate();
  const { isAuthenticated, user } = useAuth();
  const deviceId = useDeviceId();
  const anonymousSecret = useAnonymousSecret(isAuthenticated);

  const backUrl = useLocation().pathname;

  const myGamesQuery = useMyInvitesQuery(deviceId, anonymousSecret);
  const deleteMutation = useDeleteInviteMutation(deviceId, anonymousSecret);

  const del = deleteMutation.mutateAsync;
  const onDelete = useCallback((inviteId: string) => del(inviteId), [del]);

  const games = useMemo(
    (): IInviteInfo[] | undefined =>
      myGamesQuery.data?.invites.map((invite) => {
        const hostIsWhite = invite.hostSide === 'White';
        const specs: string[] = formatGameSpecs(
          {
            clockControlType: invite.clockControlType,
            whiteClockControlSettings: invite.clockControlSettings,
            blackClockControlSettings: invite.clockControlSettings,
          },
          hostIsWhite ? 'white' : 'black',
          t,
        );

        if (invite.ratingGame) {
          specs.push('Рейтинг');
        }

        const game = myGamesQuery.data.games.find((g) => g.gameId === invite.id);

        const canDelete = invite.status !== 'Playing';

        if (game) return { specs, game, created: invite.createdTimestampMs, canDelete };

        const host = invite.host ? { ...invite.host, id: invite.host.userId } : undefined;

        return {
          specs,
          game: {
            gameId: invite.id,
            currentFen: FEN_STARTING_POSITION,
            white: invite.hostSide === 'White' ? host : undefined,
            black: invite.hostSide === 'Black' ? host : undefined,
          },
          created: invite.createdTimestampMs,
          canDelete,
        };
      }),
    [myGamesQuery.data, t],
  );

  return (
    <FriendMyGamesPage
      logo={<SiteLogoWidget />}
      actions={
        <>
          <UserSettingsMenuWidget />
          <UserAuthMenuWidget />
        </>
      }
      isLoading={myGamesQuery.isPending}
      playerId={user?.userId ?? myGamesQuery.data?.anonymousId ?? undefined}
      games={games}
      backUrl={backUrl}
      onDelete={onDelete}
    />
  );
}
